/**
 * The SFU path for meeting rooms (FR-6.4, BRD §11 Q4).
 *
 * A mesh has every participant send their own media to every other one, so the
 * upload cost grows with the number of people in the room: it is fine for four
 * and impossible for a hundred. An SFU takes one upload from each person and
 * fans it out, which is what makes a room the size the BRD asks for work at all.
 *
 * The LiveKit SDK is imported dynamically so a deployment with no media server
 * never downloads it — the office map is already the heaviest thing on the page.
 *
 * Device handling belongs to LiveKit here rather than to our own getUserMedia:
 * it owns publishing, mute state and the renegotiation that follows, and having
 * two things open the same camera is how you end up with a light that will not
 * go out.
 */

export interface SfuCallbacks {
  /** Local preview: whatever we are currently publishing. */
  onLocalStream: (stream: MediaStream | null) => void;
  /**
   * Remote **video**, keyed by user id — the same shape the mesh path produces.
   * Audio is not in here: it plays through its own elements, see below.
   */
  onRemoteStreams: (streams: Map<string, MediaStream>) => void;
  /** Who the server says is audible, by user id. */
  onActiveSpeakers?: (userIds: string[]) => void;
  /** Who is sharing a screen, by user id, so it can be given the room's attention. */
  onScreenShares?: (userIds: string[]) => void;
  /** The browser refused to start audio until somebody interacts with the page. */
  onAudioBlocked?: () => void;
  onConnected: () => void;
  onClosed: (reason: string | null) => void;
}

export interface SfuSession {
  /** Retries playback the browser blocked; call it from a user gesture. */
  startAudio: () => Promise<void>;
  setMicrophoneEnabled: (on: boolean) => Promise<void>;
  setCameraEnabled: (on: boolean) => Promise<void>;
  setScreenShareEnabled: (on: boolean) => Promise<void>;
  disconnect: () => Promise<void>;
}

export interface SfuOptions {
  serverUrl: string;
  token: string;
  /** Rooms are joined with the microphone on and the camera closed. */
  microphone: boolean;
  camera: boolean;
}

export async function connectToSfu(options: SfuOptions, callbacks: SfuCallbacks): Promise<SfuSession> {
  const { Room, RoomEvent } = await import('livekit-client');

  const room = new Room({
    /*
     * Adaptive stream is off, and it has to be.
     *
     * It decides whether a video track is worth streaming by watching the
     * elements the SDK itself attached — and we attach none for video: the
     * tracks are collected into MediaStreams and rendered by our own tiles.
     * With nothing attached it sees nobody watching and never delivers the
     * video at all, which is why a shared screen arrived as silence.
     *
     * Dynacast stays on: it is publisher-side, and drops layers nobody has
     * subscribed to, which is true whatever renders them.
     */
    adaptiveStream: false,
    dynacast: true,
  });

  /**
   * Audio plays through elements the SDK makes for it, not through the video
   * tiles.
   *
   * This is the whole reason the first person into a meeting room could not be
   * heard: a tile is only in the DOM once the roster has arrived and it is
   * hidden whenever the camera is off, so whether anyone was audible came down
   * to whether a display:none <video> happened to start playing. Attaching the
   * track makes playback the SDK's problem, which is where it belongs — it
   * handles the element's attributes and the browser's autoplay rules.
   */
  const audioHost = document.createElement('div');
  audioHost.dataset.homework = 'call-audio';
  audioHost.style.position = 'fixed';
  audioHost.style.width = '1px';
  audioHost.style.height = '1px';
  audioHost.style.overflow = 'hidden';
  audioHost.style.opacity = '0';
  audioHost.style.pointerEvents = 'none';
  document.body.appendChild(audioHost);

  const publishRemote = (): void => {
    const streams = new Map<string, MediaStream>();
    const sharers: string[] = [];
    for (const participant of room.remoteParticipants.values()) {
      const published = [...participant.trackPublications.values()].filter(
        (publication) => publication.isSubscribed && publication.track?.mediaStreamTrack,
      );
      // Somebody sharing their screen with their camera on is showing you the
      // screen; a tile holding both would pick whichever came first.
      const sharing = published.some((publication) => publication.source === 'screen_share');
      const stream = new MediaStream();
      for (const publication of published) {
        const track = publication.track?.mediaStreamTrack;
        // Video only: audio is attached, and putting it in a tile as well
        // would play every voice twice.
        if (!track || track.kind !== 'video') continue;
        if (sharing && publication.source !== 'screen_share') continue;
        stream.addTrack(track);
      }
      // The identity is the user id we signed into the token, so this lines up
      // with the roster the server publishes over the socket.
      if (stream.getTracks().length > 0) streams.set(participant.identity, stream);
      if (sharing) sharers.push(participant.identity);
    }
    callbacks.onRemoteStreams(streams);
    callbacks.onScreenShares?.(sharers);
  };

  const publishLocal = (): void => {
    const publications = [...room.localParticipant.trackPublications.values()].filter(
      (publication) => publication.track?.mediaStreamTrack && !publication.isMuted,
    );
    // While sharing, the screen is what the preview shows — two video tracks in
    // one stream would leave which one appears to the element's discretion.
    const sharing = publications.some((publication) => publication.source === 'screen_share');
    const stream = new MediaStream();
    for (const publication of publications) {
      const track = publication.track?.mediaStreamTrack;
      if (!track) continue;
      if (sharing && track.kind === 'video' && publication.source !== 'screen_share') continue;
      stream.addTrack(track);
    }
    callbacks.onLocalStream(stream.getTracks().length > 0 ? stream : null);
  };

  room
    .on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind === 'audio') audioHost.appendChild(track.attach());
      publishRemote();
    })
    .on(RoomEvent.TrackUnsubscribed, (track) => {
      if (track.kind === 'audio') for (const element of track.detach()) element.remove();
      publishRemote();
    })
    .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      // The server already works out who is audible, and it is both cheaper
      // and steadier than measuring a hundred streams in the browser.
      callbacks.onActiveSpeakers?.(speakers.map((participant) => participant.identity));
    })
    .on(RoomEvent.AudioPlaybackStatusChanged, () => {
      if (!room.canPlaybackAudio) callbacks.onAudioBlocked?.();
    })
    .on(RoomEvent.TrackMuted, publishRemote)
    .on(RoomEvent.TrackUnmuted, publishRemote)
    .on(RoomEvent.ParticipantConnected, publishRemote)
    .on(RoomEvent.ParticipantDisconnected, publishRemote)
    .on(RoomEvent.LocalTrackPublished, publishLocal)
    .on(RoomEvent.LocalTrackUnpublished, publishLocal)
    .on(RoomEvent.Connected, () => {
      callbacks.onConnected();
      publishRemote();
    })
    .on(RoomEvent.Disconnected, (reason) => callbacks.onClosed(reason ? String(reason) : null));

  await room.connect(options.serverUrl, options.token);

  // Publishing after connecting, so a refused microphone leaves us in the room
  // listening rather than failing the join outright.
  if (options.microphone) await room.localParticipant.setMicrophoneEnabled(true);
  if (options.camera) await room.localParticipant.setCameraEnabled(true);
  publishLocal();

  return {
    startAudio: () => room.startAudio(),
    setMicrophoneEnabled: async (on) => {
      await room.localParticipant.setMicrophoneEnabled(on);
      publishLocal();
    },
    setCameraEnabled: async (on) => {
      await room.localParticipant.setCameraEnabled(on);
      publishLocal();
    },
    setScreenShareEnabled: async (on) => {
      await room.localParticipant.setScreenShareEnabled(on);
      publishLocal();
    },
    disconnect: async () => {
      room.removeAllListeners();
      /*
       * Stop the capture, don't just stop publishing it.
       *
       * Disconnecting unpublishes, but the device stays open until something
       * stops the track — which is why walking out of a meeting left the
       * microphone light on. Done before the await, so the device is released
       * as the person steps out rather than whenever the socket finishes
       * closing.
       */
      for (const publication of room.localParticipant.trackPublications.values()) {
        publication.track?.stop();
      }
      audioHost.remove();
      await room.disconnect();
    },
  };
}
